import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiService, formatters } from '../services/api';
import { Pool, Worker, Block, PerformanceData, MinerSettings } from '../types';
import './DashboardPage.css';

interface DashboardBlock extends Block {
  time: string;
  difficulty: number;
  shares: number;
  effort: number;
  luck: number;
  confirmations?: number | string;
  confirmationTarget?: number;
}

interface DashboardPayment {
  time: string;
  amount: number;
  address: string;
  transactionId: string;
  transactionUrl?: string;
}

interface DashboardStats {
  pendingShares: number;
  pendingBalance: number;
  lastShare: string;
  workersOnline: number;
  totalPaid: number;
  hashrate30m: number;
  hashrate3h: number;
  roundShare: number;
  poolEffort: number;
  workers: Worker[];
  payments: DashboardPayment[];
  blocks: DashboardBlock[];
  earnings: EarningPeriod[];
  todayTotal: number;
  yesterdayTotal: number;
  weeklyAverage: number;
  estimatedBlockTimeSeconds: number | null;
  estimatedBlockTimeFormatted: string;
  paymentThreshold?: number | null;
}

interface EarningPeriod {
  date: string;
  hashrate: number;
  shares: number;
  blocks: number;
  earnings: number;
  efficiency: number;
}

const DIFFICULTY_CONSTANT = 4294967296; // 2^32

const formatEstimatedTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds <= 0) {
    return 'Calculating...';
  }

  if (seconds >= 365 * 24 * 60 * 60) {
    return '1y+';
  }

  const units = [
    { label: 'd', value: 24 * 60 * 60 },
    { label: 'h', value: 60 * 60 },
    { label: 'm', value: 60 },
    { label: 's', value: 1 },
  ];

  let remainder = Math.floor(seconds);
  const parts: string[] = [];

  for (const unit of units) {
    if (remainder >= unit.value) {
      const amount = Math.floor(remainder / unit.value);
      parts.push(`${amount}${unit.label}`);
      remainder -= amount * unit.value;
    }
    if (parts.length === 2) break;
  }

  return parts.length ? parts.join(' ') : '<1m';
};

const parseDateValue = (value: string | number | undefined | null): Date | null => {
  if (value === undefined || value === null || value === '') return null;
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const workerArrayFromObject = (workersMap: Record<string, any>, fallbackLastSeen?: string): Worker[] => {
  return Object.entries(workersMap || {}).map(([name, stats]) => ({
    name,
    hashrate: stats?.hashrate || stats?.hashRate || 0,
    shares: stats?.shares ?? stats?.sharesPerSecond ?? 0,
    lastSeen: stats?.lastSeen || fallbackLastSeen || new Date().toISOString(),
    blocksFound: stats?.blocksFound ?? stats?.blocksfound ?? 0
  }));
};

const extractWorkers = (minerData: any): Worker[] => {
  if (Array.isArray(minerData?.workers)) {
    return minerData.workers.map((worker: Worker) => ({
      name: worker.name || 'Unknown',
      hashrate: worker.hashrate || 0,
      shares: worker.shares || 0,
      lastSeen: worker.lastSeen || minerData?.performance?.created || new Date().toISOString(),
      blocksFound: worker.blocksFound ?? 0
    }));
  }

  if (minerData?.workers && typeof minerData.workers === 'object') {
    return workerArrayFromObject(minerData.workers, minerData?.performance?.created);
  }

  if (minerData?.performance?.workers) {
    return workerArrayFromObject(minerData.performance.workers, minerData.performance.created);
  }

  return [];
};

const sumWorkerHashrate = (workers: Worker[]): number =>
  workers.reduce((sum, worker) => sum + (worker.hashrate || 0), 0);

const getPerformanceHashrate = (perf: PerformanceData): number => {
  if (typeof perf?.hashrate === 'number') return perf.hashrate;
  if (perf?.workers) {
    return Object.values(perf.workers).reduce((sum, worker) => sum + (worker?.hashrate || 0), 0);
  }
  return 0;
};

const getPerformanceShares = (perf: PerformanceData): number => {
  if (typeof perf?.shares === 'number') return perf.shares;
  if (perf?.workers) {
    return Object.values(perf.workers).reduce((sum, worker) => sum + (worker?.shares ?? worker?.sharesPerSecond ?? 0), 0);
  }
  return 0;
};

const formatDifficulty = (value: number): string => {
  if (!value || !isFinite(value)) return '0';
  const units = ['', 'K', 'M', 'G', 'T', 'P', 'E'];
  let unitIndex = 0;
  let normalized = value;

  while (normalized >= 1000 && unitIndex < units.length - 1) {
    normalized /= 1000;
    unitIndex++;
  }

  const decimals = normalized >= 100 ? 0 : 2;
  const suffix = units[unitIndex];
  return `${normalized.toFixed(decimals)}${suffix ? ' ' + suffix : ''}`;
};

const DashboardPage: React.FC = () => {
  const [walletAddress, setWalletAddress] = useState('');
  const [selectedPoolId, setSelectedPoolId] = useState<string>('');
  const [pools, setPools] = useState<Pool[]>([]);
  const [minerStats, setMinerStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('workers');
  const [thresholdInput, setThresholdInput] = useState('');
  const [ipAddressInput, setIpAddressInput] = useState('');
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [settingsMessageType, setSettingsMessageType] = useState<'success' | 'error' | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [ipLookupLoading, setIpLookupLoading] = useState(false);
  const selectedPool = pools.find(pool => pool.id === selectedPoolId) || null;
  const selectedCoinLabel = selectedPool?.coinSymbol || selectedPool?.coin || '';
  const defaultPaymentThreshold = selectedPool?.minimumPayment ?? null;
  const effectivePaymentThreshold =
    minerStats?.paymentThreshold ?? defaultPaymentThreshold ?? null;

  // Load available pools on component mount and handle URL parameters
  useEffect(() => {
    const loadPools = async () => {
      try {
        const poolData = await apiService.getPools();
        setPools(poolData);
        
        // Check for parameters from sessionStorage (from homepage navigation)
        const poolParam = sessionStorage.getItem('dashboardPool');
        const addressParam = sessionStorage.getItem('dashboardAddress');
        
        console.log('Pool param from storage:', poolParam);
        console.log('Address param from storage:', addressParam);
        
        // Clear the values after reading them
        if (poolParam && addressParam) {
          sessionStorage.removeItem('dashboardPool');
          sessionStorage.removeItem('dashboardAddress');
        }
        
        if (poolParam && addressParam) {
          // Set parameters from URL
          setSelectedPoolId(poolParam);
          setWalletAddress(addressParam);
          
          // Auto-load miner data if parameters are provided
          setTimeout(async () => {
            await loadMinerData(poolParam, addressParam, poolData);
          }, 100);
        } else if (poolData.length > 0) {
          setSelectedPoolId(poolData[0].id); // Set first pool as default
        }
      } catch (err) {
        console.error('Failed to load pools:', err);
      }
    };
    
    loadPools();
  }, []);

  useEffect(() => {
    if (minerStats?.paymentThreshold !== undefined && minerStats?.paymentThreshold !== null) {
      setThresholdInput(String(minerStats.paymentThreshold));
    } else if (selectedPool?.minimumPayment !== undefined && selectedPool?.minimumPayment !== null) {
      setThresholdInput(String(selectedPool.minimumPayment));
    } else {
      setThresholdInput('');
    }
  }, [minerStats?.paymentThreshold, selectedPool?.minimumPayment]);

  const getPoolTicker = (pool: Pool) => {
    if (pool.coinSymbol) return pool.coinSymbol.toUpperCase();
    if (pool.coin) {
      const compact = pool.coin.replace(/[^a-z0-9]/gi, '').slice(0, 4);
      return compact ? compact.toUpperCase() : '';
    }
    return '';
  };

  const loadMinerData = async (poolId: string, address: string, poolList: Pool[] = pools) => {
    setLoading(true);
    setError(null);
    
    try {
      // Get miner stats from API
      const minerData = await apiService.getMinerStats(poolId, address);
      
      // Get additional data
      const [paymentsResponse, blocksResponse, performanceResponse, settingsResponse] = await Promise.all([
        apiService.getMinerPayments(poolId, address, 50),
        apiService.getMinerBlocks(poolId, address, 25),
        apiService.getMinerPerformance(poolId, address, 30),
        apiService.getMinerSettings(poolId, address)
      ]);
      const normalizeCollection = (data: any): any[] => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
        return [];
      };
      const normalizePerformance = (data: any): PerformanceData[] => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.data)) return data.data;
        if (typeof data === 'object') return Object.values(data);
        return [];
      };
      const payments = normalizeCollection(paymentsResponse);
      const blocks = normalizeCollection(blocksResponse);
      const performance = normalizePerformance(performanceResponse);
      const minerSettings = settingsResponse as MinerSettings | null;
      const rawThreshold =
        minerSettings?.paymentThreshold ??
        minerSettings?.PaymentThreshold ??
        minerSettings?.settings?.PaymentThreshold ??
        (minerData as any)?.paymentThreshold;
      const normalizedPaymentThreshold =
        rawThreshold !== null && rawThreshold !== undefined && rawThreshold !== ''
          ? Number(rawThreshold)
          : null;
      const paymentThresholdValue =
        normalizedPaymentThreshold !== null && Number.isFinite(normalizedPaymentThreshold)
          ? normalizedPaymentThreshold
          : null;

      const poolInfo = poolList.find(pool => pool.id === poolId);

      // Calculate additional stats
      const workerList = extractWorkers(minerData as any);

      let workersOnline = workerList.filter(worker => {
        const lastSeenTime = parseDateValue(worker.lastSeen)?.getTime();
        if (!lastSeenTime) return true;
        const now = Date.now();
        const fifteenMinutes = 15 * 60 * 1000;
        return (now - lastSeenTime) < fifteenMinutes;
      }).length;
      if (workersOnline === 0 && workerList.length > 0) {
        workersOnline = workerList.length;
      }

      // Calculate payment statistics
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const getPaymentDate = (payment: any) => parseDateValue(payment.timestamp || payment.created);

      const todayPayments = payments.filter(p => {
        const paymentDate = getPaymentDate(p);
        return paymentDate ? paymentDate >= today : false;
      });
      const yesterdayPayments = payments.filter(p => {
        const paymentDate = getPaymentDate(p);
        return paymentDate ? paymentDate >= yesterday && paymentDate < today : false;
      });
      const weekPayments = payments.filter(p => {
        const paymentDate = getPaymentDate(p);
        return paymentDate ? paymentDate >= weekAgo : false;
      });

      const extractAmount = (payment: any) => Number(payment.amount || payment.value || 0);
      const todayTotal = todayPayments.reduce((sum, p) => sum + extractAmount(p), 0);
      const yesterdayTotal = yesterdayPayments.reduce((sum, p) => sum + extractAmount(p), 0);
      const weeklyAverage = weekPayments.length > 0
        ? weekPayments.reduce((sum, p) => sum + extractAmount(p), 0) / 7
        : 0;

      const minerHashrate30m = sumWorkerHashrate(workerList);
      const minerHashrate3h = performance.length > 0 ? getPerformanceHashrate(performance[0]) : minerHashrate30m;

      // Generate earnings history from performance data
      const earnings: EarningPeriod[] = performance.slice(0, 7).map(perf => {
        const dateObj = parseDateValue(perf.timestamp || perf.created);
        const dayPayments = payments.filter(p => {
          const paymentDate = parseDateValue(p.timestamp);
          return paymentDate && dateObj ? paymentDate.toDateString() === dateObj.toDateString() : false;
        });
        const dayEarnings = dayPayments.reduce((sum, p) => sum + p.amount, 0);
        
        return {
          date: dateObj ? dateObj.toISOString().split('T')[0] : 'N/A',
          hashrate: getPerformanceHashrate(perf),
          shares: Math.round(getPerformanceShares(perf)),
          blocks: blocks.filter(b => {
            const blockDate = parseDateValue(b.timestamp);
            return blockDate && dateObj ? blockDate.toDateString() === dateObj.toDateString() : false;
          }).length,
          earnings: dayEarnings,
          efficiency: Math.min(100, Math.max(0, ((perf.shares || 0) / Math.max(1, (perf.hashrate || 0) / 1000000)) * 100)) // Calculate efficiency
        };
      });

      let estimatedBlockTimeSeconds: number | null = null;
      let estimatedBlockTimeFormatted = 'N/A';

      const networkHashrate = poolInfo?.stats?.networkhashrate;
      const poolHashrate = poolInfo?.hashrate;
      const blockTime = poolInfo?.stats?.blockTime || poolInfo?.stats?.timeToFindBlock || 0;
      const networkDifficulty = poolInfo?.stats?.networkdifficulty;
      const minerHashrate = minerData.hashrate || minerHashrate30m;

      if (networkHashrate && poolHashrate && blockTime > 0) {
        estimatedBlockTimeSeconds = (networkHashrate / poolHashrate) * blockTime;
      } else if (poolInfo?.stats?.timeToFindBlock && poolInfo.hashrate && minerHashrate > 0) {
        const poolTime = poolInfo.stats.timeToFindBlock;
        estimatedBlockTimeSeconds = poolTime * (poolInfo.hashrate / minerHashrate);
      } else if (networkDifficulty && minerHashrate > 0) {
        estimatedBlockTimeSeconds = (networkDifficulty * DIFFICULTY_CONSTANT) / minerHashrate;
      }

      if (estimatedBlockTimeSeconds) {
        estimatedBlockTimeFormatted = formatEstimatedTime(estimatedBlockTimeSeconds);
      }

      const dashboardStats: DashboardStats = {
        pendingShares: minerData.pendingShares || 0,
        pendingBalance: minerData.pendingBalance ?? minerData.balance ?? 0,
        lastShare: formatters.timeAgo(minerData.lastActivity || (minerData as any).performance?.created),
        workersOnline,
        totalPaid: minerData.totalPaid || 0,
        hashrate30m: minerHashrate30m,
        hashrate3h: minerHashrate3h,
        roundShare: ((minerData as any).minerEffort || 0) * 100,
        poolEffort: (minerData as any)?.poolEffort || (poolInfo as any)?.poolEffort || 0,
        paymentThreshold: paymentThresholdValue,
        workers: workerList,
        payments: payments.map(p => {
          const transactionUrl =
            p.transactionInfoLink ||
            p.transactionInfo?.link ||
            p.txLink ||
            p.paymentLink ||
            p.transactionConfirmationDataLink ||
            '';
          const transactionId =
            p.transactionConfirmationData ||
            p.txId ||
            p.txHash ||
            p.transactionId ||
            '';

          return {
            time: formatters.formatDate(p.timestamp || p.created),
            amount: extractAmount(p),
            address: p.address || address,
            transactionUrl,
            transactionId
          };
        }),
        blocks: blocks.map(b => {
          const timestamp = (b as any).timestamp || (b as any).created || (b as any).timeFound;
          const heightValue = (b as any).height ?? (b as any).blockHeight ?? (b as any).blockheight;
          const effortValue = Number((b as any).effort || 0);
          const confirmationsValue =
            (b as any).confirmations ??
            (b as any).confirmationProgress ??
            (b as any).confirmationPercent ??
            null;
          const confirmationPercent =
            typeof confirmationsValue === 'number' && confirmationsValue <= 1
              ? confirmationsValue * 100
              : confirmationsValue;
          const targetConfirmations =
            (b as any).requiredConfirmations ||
            (b as any).targetConfirmations ||
            60;

          return {
            ...b,
            time: formatters.formatDate(timestamp),
            height: Number(heightValue || 0),
            difficulty: Number((b as any).networkDifficulty || (b as any).difficulty || 0),
            shares: Number((b as any).shares || 0),
            effort: effortValue * 100,
            luck: Number((b as any).luck || 0),
            reward: Number((b as any).reward || (b as any).amount || 0),
            confirmations: confirmationPercent ?? null,
            confirmationTarget: targetConfirmations
          };
        }),
        earnings,
        todayTotal,
        yesterdayTotal,
        weeklyAverage,
        estimatedBlockTimeSeconds,
        estimatedBlockTimeFormatted
      };
      
      setMinerStats(dashboardStats);
    } catch (err) {
      console.error('Failed to load miner statistics:', err);
      if (axios.isAxiosError(err)) {
        const message = err.response?.data?.message || err.response?.statusText || err.message;
        setError(`Failed to load miner statistics: ${message}`);
      } else if (err instanceof Error) {
        setError(`Failed to load miner statistics: ${err.message}`);
      } else {
        setError('Failed to load miner statistics. Please check your wallet address and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWalletSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress.trim() || !selectedPoolId) return;
    
    await loadMinerData(selectedPoolId, walletAddress);
  };

  const handleThresholdSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!minerStats) {
      setSettingsMessage('Load your miner statistics before updating settings.');
      setSettingsMessageType('error');
      return;
    }

    const normalizedAddress = walletAddress.trim();
    if (!selectedPoolId || !normalizedAddress) {
      setSettingsMessage('Select a pool and enter your wallet address first.');
      setSettingsMessageType('error');
      return;
    }

    if (!minerStats.workers || minerStats.workers.length === 0) {
      setSettingsMessage('You need at least one active worker to change your payment threshold.');
      setSettingsMessageType('error');
      return;
    }

    const thresholdValue = Number(thresholdInput);
    if (!Number.isFinite(thresholdValue) || thresholdValue <= 0) {
      setSettingsMessage('Enter a valid payment threshold greater than zero.');
      setSettingsMessageType('error');
      return;
    }

    const ipAddress = ipAddressInput.trim();
    if (!ipAddress) {
      setSettingsMessage('Enter the IP address of the miner you want to authorize.');
      setSettingsMessageType('error');
      return;
    }

    try {
      setSettingsLoading(true);
      setSettingsMessage(null);
      setSettingsMessageType(null);
      const response = await apiService.updateMinerSettings(
        selectedPoolId,
        normalizedAddress,
        thresholdValue,
        ipAddress
      );
      const successMessage = response?.message || 'Custom payment threshold updated successfully.';
      setSettingsMessage(successMessage);
      setSettingsMessageType('success');
      setMinerStats(prev => prev ? { ...prev, paymentThreshold: thresholdValue } : prev);
    } catch (updateError: any) {
      console.error('Failed to update miner settings:', updateError);
      const errorMessage =
        updateError?.response?.data?.message ||
        updateError?.message ||
        'Failed to update payment threshold. Please verify your IP address and try again.';
      setSettingsMessage(errorMessage);
      setSettingsMessageType('error');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDetectIp = async () => {
    try {
      setIpLookupLoading(true);
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) {
        throw new Error('Unable to detect IP address.');
      }
      const data = await response.json();
      if (data?.ip) {
        setIpAddressInput(data.ip);
        setSettingsMessage('Detected your public IP address.');
        setSettingsMessageType('success');
      } else {
        throw new Error('Invalid response from IP service.');
      }
    } catch (detectError: any) {
      console.error('Failed to detect IP address:', detectError);
      setSettingsMessage(detectError?.message || 'Failed to detect IP address.');
      setSettingsMessageType('error');
    } finally {
      setIpLookupLoading(false);
    }
  };

  return (
    <div className="dashboard-page">
      {/* Wallet Input */}
      <div className="wallet-input-container">
        <form onSubmit={handleWalletSubmit}>
          <select
            value={selectedPoolId}
            onChange={(e) => setSelectedPoolId(e.target.value)}
            className="pool-select"
            required
          >
            <option value="">Select Pool</option>
            {pools.map(pool => (
              <option key={pool.id} value={pool.id}>
                {pool.coin} ({getPoolTicker(pool)}) · {pool.paymentType || 'PPLNS'}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            className="wallet-input"
            placeholder="Enter your wallet address"
            required
          />
          <button type="submit" className="btn-primary" disabled={loading || !selectedPoolId}>
            {loading ? 'Loading...' : 'Load Stats'}
          </button>
        </form>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {minerStats && (
        <>
          {/* Main Stats Grid */}
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">⏳</span>
                Pending Info
              </div>
              <div className="pending-info">
                <div className="pending-info-row">
                  <span className="pending-info-label">Shares:</span>
                  <span className="pending-info-value">{(minerStats.pendingShares || 0).toLocaleString()}</span>
                </div>
                <div className="pending-info-row">
                  <span className="pending-info-label">Coins:</span>
                  <span className="pending-info-value">{(minerStats.pendingBalance || 0).toFixed(8)}</span>
                </div>
              </div>
              <div className="stat-description">Preliminary balance awaiting blocks to mature.</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">🕒</span>
                Last Share Submitted
              </div>
              <div className="stat-value blue">{minerStats.lastShare}</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">💰</span>
                Pending Balance
              </div>
              <div className="stat-value">{(minerStats.pendingBalance || 0).toFixed(8)}</div>
              <div className="stat-description">Credited coins awaiting payout.</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">👥</span>
                Workers Online
              </div>
              <div className="stat-value">{minerStats.workersOnline || 0}</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">💵</span>
                Total Paid
              </div>
              <div className="stat-value">{(minerStats.totalPaid || 0).toFixed(8)}</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">⚡</span>
                Hashrate (30m)
              </div>
              <div className="stat-value green">{formatters.hashrate(minerStats.hashrate30m)}</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">📈</span>
                Hashrate (3h)
              </div>
              <div className="stat-value green">{formatters.hashrate(minerStats.hashrate3h)}</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">⛏️</span>
                Est. Time to Block
              </div>
              <div className={`stat-value ${minerStats.estimatedBlockTimeSeconds ? 'highlight' : ''}`}>
                {minerStats.estimatedBlockTimeFormatted}
              </div>
              <div className="stat-description">Estimated from current pool hashrate and network stats.</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">📊</span>
                Your Round Share
              </div>
              <div className="stat-value">{(minerStats.roundShare || 0).toFixed(6)}%</div>
              <div className="stat-description">Percent of your contribution to current round.</div>
            </div>

            <div className="stat-item">
              <div className="stat-label">
                <span className="stat-icon">⚙️</span>
                Pool Effort
              </div>
              <div className="stat-value">{(minerStats.poolEffort || 0).toFixed(1)}%</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tab-nav">
            <button
              className={`tab-link ${activeTab === 'workers' ? 'active' : ''}`}
              onClick={() => setActiveTab('workers')}
            >
              👨‍💻 Workers
              <span className="tab-badge">{minerStats.workers.length}</span>
            </button>
            <button
              className={`tab-link ${activeTab === 'blocks' ? 'active' : ''}`}
              onClick={() => setActiveTab('blocks')}
            >
              🧱 Blocks
            </button>
            <button
              className={`tab-link ${activeTab === 'payouts' ? 'active' : ''}`}
              onClick={() => setActiveTab('payouts')}
            >
              💰 Payouts
            </button>
            <button
              className={`tab-link ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              ⚙️ Settings
            </button>
          </div>

          {/* Workers Tab */}
          {activeTab === 'workers' && (
            <div className="tab-content">
              <h3>Your Workers</h3>
              
              <div className="table-container">
                <table className="workers-table">
                  <thead>
                    <tr>
                      <th>Worker Name</th>
                      <th>Hashrate</th>
                      <th>Shares</th>
                      <th style={{ textAlign: 'center' }}>Blocks Found</th>
                      <th>Last Seen</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {minerStats.workers.map((worker, index) => {
                      const lastSeenTime = new Date(worker.lastSeen).getTime();
                      const now = Date.now();
                      const fifteenMinutes = 15 * 60 * 1000;
                      const isOnline = (now - lastSeenTime) < fifteenMinutes;
                      
                      return (
                        <tr key={`${worker.name}-${index}`} className={!isOnline ? 'worker-offline' : ''}>
                          <td>{worker.name}</td>
                          <td>{formatters.hashrate(worker.hashrate)}</td>
                          <td>{worker.shares.toLocaleString()}</td>
                          <td style={{ textAlign: 'center' }}>{(worker.blocksFound ?? 0).toLocaleString()}</td>
                          <td>{formatters.timeAgo(worker.lastSeen)}</td>
                          <td>
                            <span className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
                              {isOnline ? 'Online' : 'Offline'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="info-box">
                <p>Your average hashrate will be smoothly adjusted until you have shares to fulfill estimation window.</p>
                <p>There are two windows: short (30 minutes) and long (3 hours) for accurate hashrate calculation.</p>
                <p>Dead workers will be highlighted if they haven't submitted a share for 15 minutes.</p>
              </div>
            </div>
          )}

          {/* Payouts Tab */}
          {activeTab === 'payouts' && (
            <div className="tab-content">
              <h3>Your Payouts</h3>

              {/* Payment Statistics */}
              <div className="payment-stats-box">
                <div className="payment-stats-grid">
                  <div className="payment-stat">
                    <div className="payment-stat-label">Today's Total</div>
                    <div className="payment-stat-value">{(minerStats.todayTotal || 0).toFixed(8)}</div>
                    <div className="payment-stat-description">Total paid today</div>
                  </div>
                  <div className="payment-stat">
                    <div className="payment-stat-label">Yesterday's Total</div>
                    <div className="payment-stat-value">{(minerStats.yesterdayTotal || 0).toFixed(8)}</div>
                    <div className="payment-stat-description">Total paid yesterday</div>
                  </div>
                  <div className="payment-stat">
                    <div className="payment-stat-label">7-Day Average</div>
                    <div className="payment-stat-value">{(minerStats.weeklyAverage || 0).toFixed(8)}</div>
                    <div className="payment-stat-description">Average daily payment</div>
                  </div>
                </div>
              </div>

              <div className="table-container">
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Amount</th>
                      <th>Address</th>
                      <th>Transaction ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {minerStats.payments.map((payment, index) => (
                      <tr key={index}>
                        <td>{payment.time}</td>
                        <td>{(payment.amount || 0).toFixed(8)}</td>
                        <td className="address-cell">{payment.address || 'N/A'}</td>
                        <td className="tx-cell">
                          {payment.transactionUrl ? (
                            <a href={payment.transactionUrl} target="_blank" rel="noopener noreferrer">
                              View Tx
                            </a>
                          ) : (
                            payment.transactionId || 'N/A'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Blocks Tab */}
          {activeTab === 'blocks' && (
            <div className="tab-content">
              <h3>Blocks Found</h3>
              
              <div className="table-container">
                <table className="blocks-table">
                  <thead>
                    <tr>
                      <th>Found Time</th>
                      <th>Height</th>
                      <th>Net Diff</th>
                      <th>Effort</th>
                      <th>Reward</th>
                      <th>Status</th>
                      <th>Confirmation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {minerStats.blocks.map((block, index) => (
                      <tr key={index} className={block.status === 'orphaned' ? 'block-orphaned' : ''}>
                        <td>{block.time}</td>
                        <td>{Number(block.height || 0).toLocaleString()}</td>
                        <td>{formatDifficulty(Number(block.difficulty || 0))}</td>
                        <td>{(block.effort || 0).toFixed(2)}%</td>
                        <td>{(block.reward || 0).toFixed(8)}</td>
                        <td>
                          <span className={`status-badge ${block.status}`}>
                            {block.status === 'confirmed' ? 'Confirmed' : 
                             block.status === 'pending' ? 'Pending' : 'Orphaned'}
                          </span>
                        </td>
                        <td>
                          {block.confirmations !== null && block.confirmations !== undefined
                            ? `${Math.min(100, Math.max(0, Number(block.confirmations))).toFixed(0)}%`
                            : block.status === 'confirmed'
                              ? '100%'
                              : block.status === 'pending'
                                ? '0%'
                                : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="info-box">
                <p><strong>Confirmed:</strong> Block has been accepted by the network and rewards have been distributed.</p>
                <p><strong>Pending:</strong> Block is waiting for network confirmation (usually 60+ confirmations).</p>
                <p><strong>Orphaned:</strong> Block was rejected by the network, no rewards distributed.</p>
                <p><strong>Luck:</strong> Percentage showing how lucky/unlucky the block find was (lower is better).</p>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="tab-content">
              <h3>Miner Settings</h3>
              <div className="settings-card">
                <div className="settings-current">
                  <div className="settings-label">Current Payment Threshold</div>
                  <div className="settings-value">
                    {effectivePaymentThreshold !== null
                      ? `${effectivePaymentThreshold.toLocaleString()}${selectedCoinLabel ? ` ${selectedCoinLabel}` : ''}`
                      : 'Not set'}
                  </div>
                  <p className="settings-hint">
                    Customize when payouts occur for your wallet. The pool may require at least one active worker.
                  </p>
                  {defaultPaymentThreshold !== null && minerStats.paymentThreshold == null && (
                    <p className="settings-default">
                      Pool default: {defaultPaymentThreshold.toLocaleString()}
                      {selectedCoinLabel ? ` ${selectedCoinLabel}` : ''}
                    </p>
                  )}
                </div>
                <form className="settings-form" onSubmit={handleThresholdSubmit}>
                  <div className="settings-field">
                    <label htmlFor="thresholdInput">Custom Payment Threshold</label>
                    <input
                      id="thresholdInput"
                      type="number"
                      min="0"
                      step="any"
                      value={thresholdInput}
                      onChange={(e) => setThresholdInput(e.target.value)}
                      placeholder="e.g. 100"
                    />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="ipAddressInput">Authorized IP Address</label>
                    <div className="ip-input-group">
                      <input
                        id="ipAddressInput"
                        type="text"
                        value={ipAddressInput}
                        onChange={(e) => setIpAddressInput(e.target.value)}
                        placeholder="123.45.67.89"
                      />
                      <button
                        type="button"
                        className="btn-secondary detect-ip-btn"
                        onClick={handleDetectIp}
                        disabled={ipLookupLoading}
                      >
                        {ipLookupLoading ? 'Detecting…' : 'Use My IP'}
                      </button>
                    </div>
                  </div>
                  {settingsMessage && (
                    <div className={`settings-alert ${settingsMessageType || ''}`}>
                      {settingsMessage}
                    </div>
                  )}
                  <div className="settings-actions">
                    <button type="submit" className="btn-primary" disabled={settingsLoading}>
                      {settingsLoading ? 'Saving...' : 'Update Threshold'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
};

export default DashboardPage;
